import { Col, Row } from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import { useParams } from "react-router-dom";

import { useGetQuotationFormConfig } from "api/queries/quotation-form/useGetQuotationFormConfig";
import { dimensionUnitOptions, weightUnitOptions } from "constants/Common";
import { getServiceInputVisibility } from "helpers/quotationFormHelper";
import { QuotationFreightModeOption } from "types/Common";

export default function FreightModeSummary() {
  const { watch } = useFormContext();
  const id = useParams().id?.toUpperCase();

  const { data: quotationConfigData } = useGetQuotationFormConfig(id!, {
    enabled: !!id,
    refetchOnMount: false,
  });

  const freightModeOptions: QuotationFreightModeOption[] = quotationConfigData?.freight_modes ?? [];
  const selectedFreightModeObject = freightModeOptions.find((mode) => mode.value === watch("service.freight_mode"));

  const roadFreightTypeOptions = selectedFreightModeObject?.options ?? [];
  const selectedRoadFreightType = roadFreightTypeOptions.find((type) => type.value === watch("service.rf_type"));

  const { showChargeableWeight, showRoadFreightType, showMaxDimensions, showMaxWeight, showMaxAllowedWeight } =
    getServiceInputVisibility(selectedFreightModeObject);

  return (
    <>
      <div className="mb-4 font-weight-bold">
        <FormattedMessage id="quotation-form.freightMode" defaultMessage="Freight mode" />
      </div>

      <div className="font-b3 space-y-2">
        <Row>
          <Col className="text-secondary">
            <FormattedMessage id="quotation-form.freightMode" defaultMessage="Freight mode" />
          </Col>
          <Col>{selectedFreightModeObject?.label}</Col>
        </Row>

        {showChargeableWeight && (
          <Row>
            <Col className="text-secondary">
              <FormattedMessage id="quotation-form.chargeableWeight" defaultMessage="Chargeable weight" />
            </Col>
            <Col>
              {watch("service.af_chargeable_wt")}{" "}
              {weightUnitOptions.find((d) => d.value === watch("service.af_chargeable_wt_unit"))?.label}
            </Col>
          </Row>
        )}

        {showRoadFreightType && (
          <Row>
            <Col className="text-secondary">
              <FormattedMessage id="quotation-form.roadFreightType" defaultMessage="Road freight type" />
            </Col>
            <Col>{selectedRoadFreightType?.label}</Col>
          </Row>
        )}

        {showMaxDimensions && (
          <Row>
            <Col className="text-secondary">
              <FormattedMessage id="quotation-form.maximumDimensions" defaultMessage="Maximum dimensions" />
            </Col>
            <Col>
              {watch("service.rf_max_dimensions")}{" "}
              {dimensionUnitOptions.find((d) => d.value === watch("service.rf_max_dimensions_unit"))?.label}
            </Col>
          </Row>
        )}

        {showMaxWeight && (
          <Row>
            <Col className="text-secondary">
              <FormattedMessage id="quotation-form.chargeableWeight" defaultMessage="Chargeable weight" />
            </Col>
            <Col>
              {watch("service.rf_max_wt")}{" "}
              {weightUnitOptions.find((d) => d.value === watch("service.rf_max_wt_unit"))?.label}
            </Col>
          </Row>
        )}

        {showMaxAllowedWeight && (
          <Row>
            <Col className="text-secondary">
              <FormattedMessage
                id="quotation-form.maximumAllowedWeightPerItem"
                defaultMessage="Maximum allowed weight per item"
              />
            </Col>
            <Col>
              {watch("service.obc_max_wt_allowed")}{" "}
              {weightUnitOptions.find((d) => d.value === watch("service.obc_max_wt_allowed_unit"))?.label}
            </Col>
          </Row>
        )}
      </div>
    </>
  );
}
